import { createApi } from "@reduxjs/toolkit/query/react";
import { QueryRequest, RetrieveShopperSignatureRequest, SignatureToken, TargetPayShopperSignatureResponse, ValidatedSignatureToken } from "../../types/types";
import { baseQueryWithRefreshTokenHandler } from "../authFetchBaseQuery";

/**
 * API service for validating signature tokens and handling refresh tokens.
 * 
 * This service uses Redux Toolkit's `createApi` to define the API endpoints and their configurations.
 * It provides the `validateSignature` endpoint which sends a POST request to the API to validate a signature token.
 */
export const validateSignature = createApi({
  // The reducer path used to slice the state in Redux
  reducerPath: "validateSignature",
  
  // Custom base query handler to handle both the API requests and refresh token logic
  baseQuery: baseQueryWithRefreshTokenHandler,
  
  // Refetch data when component mounts or when arguments change
  refetchOnMountOrArgChange: true,

  // Define API endpoints and their configurations
  endpoints: (builder) => ({
    /**
     * Endpoint for validating a signature token.
     * 
     * This endpoint makes a POST request to the `AdyenSignature/ValidateSignatureToken` endpoint
     * and expects the signature token in the request body. Upon successful validation, 
     * the API returns the validated signature token data.
     * 
     * @param body - The signature token object containing the token to be validated.
     * @returns The validated signature token data.
     */
    validateSignature: builder.query<ValidatedSignatureToken, SignatureToken>({
      query: (body) => ({
        url: 'AdyenSignature/ValidateSignatureToken',  // URL of the API endpoint
        method: 'POST',  // HTTP method for the request
        body,  // The request body contains the signature token
      }),

      // Keep the data for a minimal time to reduce unnecessary re-fetching
      keepUnusedDataFor: 0,
    }),

    /**
     * Endpoint for getting the dealerId from signature transaction token.
     * 
     * This endpoint makes a POST request to the `Everest/query` endpoint
     * and expects the signature token in the request body. Upon successful validation, 
     * the API returns the associated dealer id.
     * 
     * @param token - The signature token string.
     * @returns The associated dealer id.
     */
    getTransactionDealerId: builder.query({
      query: (token: string) => {
        if (token.length > 50) return;
        const queryRequest: QueryRequest = {
          query: 'Gravity.GetTransactionDealerId',  // Query as a string
          queryBody: {
            TransactionToken: token
          }
        };
        return {
          url: 'Everest/query',
          method: 'POST',
          body: JSON.stringify(queryRequest),
        };
      },

      // Keep the data for a minimal time to reduce unnecessary re-fetching
      keepUnusedDataFor: 0,
    }),

    /**
     * Endpoint for getting the dealer credentials using given associated dealer id.
     * 
     * This endpoint makes a GET request to the `EmergePay/GetDealerCredential` endpoint
     * and expects the dealer id in the request params. Upon successful validation, 
     * the API returns the associated dealer credentials.
     * 
     * @param token - The signature token string.
     * @returns The associated dealer id.
     */
    getDealerCredential: builder.query({
      query: (dealerId) => ({
        url: `EmergePay/GetDealerCredential/${dealerId}`,
        method: 'GET',
      }),

      // Keep the data for a minimal time to reduce unnecessary re-fetching
      keepUnusedDataFor: 0,
    }),

    /**
    * Endpoint for getting the signature status from the server side..
    */
    RetrieveShopperSignature: builder.query<TargetPayShopperSignatureResponse,RetrieveShopperSignatureRequest>({
      query: (request) => ({
        url: `AdyenSignature/RetrieveShopperSignature?DealerId=${request.dealerId}&CustomerId=${request.customerId}`,  // URL of the API endpoint
        method: 'GET',  // HTTP method for the request
      }),

      // Keep the data for a minimal time to reduce unnecessary re-fetching
      keepUnusedDataFor: 0,
    }),
  }),
});

// Export the auto-generated hook from the `validateSignature` API slice
export const { useValidateSignatureQuery, useGetDealerCredentialQuery, useGetTransactionDealerIdQuery, useRetrieveShopperSignatureQuery } = validateSignature;
